<template>
  <div class="animated fadeIn">
    <!-- Top Control Section -->
    <b-row class="mb-4">
      <!-- Add Entry Card -->
      <b-col xl="8" lg="12">
        <b-card no-body class="h-100">
          <b-card-header>
            <i class="fa fa-user-plus text-primary"></i> <strong>Add Player(s) to Watchlist</strong>
          </b-card-header>
          <b-card-body>
            <b-form @submit.prevent="AddWatchlist">
              <b-row>
                <b-col md="12" class="mb-3">
                  <b-form-group label="Usernames" label-for="usernames-input" label-class="font-weight-bold small text-muted text-uppercase">
                    <b-form-textarea
                      id="usernames-input"
                      placeholder="Enter usernames, one per line (e.g. Player1, Player2...)"
                      v-model="userr"
                      rows="2"
                      no-resize
                      required
                    ></b-form-textarea>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="5">
                  <b-form-group label="Reason" label-for="reason-select" label-class="font-weight-bold small text-muted text-uppercase">
                    <b-input-group>
                      <b-input-group-prepend is-text><i class="fa fa-tag"></i></b-input-group-prepend>
                      <b-form-select
                        id="reason-select"
                        v-model="reason"
                        :options="options"
                        required
                      ></b-form-select>
                    </b-input-group>
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group label="Initial Note" label-for="note-input" label-class="font-weight-bold small text-muted text-uppercase">
                    <b-input-group>
                      <b-input-group-prepend is-text><i class="fa fa-sticky-note-o"></i></b-input-group-prepend>
                      <b-form-input
                        id="note-input"
                        v-model="note"
                        placeholder="Optional context..."
                      ></b-form-input>
                    </b-input-group>
                  </b-form-group>
                </b-col>
                <b-col md="3" class="d-flex align-items-center">
                  <b-form-checkbox v-model="priority" name="priority-check" class="mt-2 font-weight-bold text-danger">
                    <i class="fa fa-star"></i> High Priority
                  </b-form-checkbox>
                </b-col>
              </b-row>
              <div class="text-right mt-2">
                <b-button type="submit" variant="primary" :disabled="!reason || !userr || !hasPermission(WatchlistAdd)">
                  <i class="fa fa-plus-circle"></i> Add to Watchlist
                </b-button>
              </div>
            </b-form>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- Stats & Quick Filters Card -->
      <b-col xl="4" lg="12">
        <b-card no-body class="h-100">
          <b-card-header>
            <i class="fa fa-dashboard text-info"></i> <strong>List Overview</strong>
          </b-card-header>
          <b-card-body class="d-flex flex-column justify-content-between">
            <div class="mb-3">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <span class="small text-uppercase font-weight-bold text-muted">Total Entries</span>
                <b-badge variant="info" pill class="px-3" v-if="watchlistData">{{ watchlistData.length }}</b-badge>
              </div>
              <hr class="mt-1 mb-3 border-secondary" />
              
              <b-form-group label="Online Filter" label-class="font-weight-bold small text-muted text-uppercase">
                <b-form-radio-group
                  v-model="filterType"
                  @change="activeExternalFilter"
                  :options="serverOptions"
                  buttons
                  button-variant="outline-secondary"
                  size="sm"
                  name="server-filter"
                  class="w-100 dark-radio-group mb-2"
                ></b-form-radio-group>
              </b-form-group>

              <b-button :variant="showOnlyPriority ? 'danger' : 'outline-danger'" size="sm" block @click="togglePriorityFilter">
                <i class="fa" :class="showOnlyPriority ? 'fa-star' : 'fa-star-o'"></i> 
                {{ showOnlyPriority ? 'Showing High Priority Only' : 'Show All Priorities' }}
              </b-button>
            </div>

            <div class="p-2 bg-dark rounded border border-secondary border-dashed small text-muted">
              <i class="fa fa-info-circle text-info mr-1"></i>
              Selection actions appear in the table toolbar when items are picked.
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Table Section -->
    <b-row>
      <b-col cols="12">
        <b-card no-body class="shadow-sm">
          <b-card-header class="d-flex justify-content-between align-items-center py-2">
            <div>
              <i class="fa fa-table text-warning"></i> <strong>Current Watchlist</strong>
            </div>
            
            <!-- Grid Toolbar -->
            <div class="d-flex align-items-center">
              <b-input-group size="sm" class="grid-search-group mr-2">
                <b-input-group-prepend is-text class="bg-dark border-secondary"><i class="fa fa-search small"></i></b-input-group-prepend>
                <b-form-input
                  v-model="quickFilterText"
                  placeholder="Quick search..."
                  class="bg-dark text-white border-secondary"
                  @input="onQuickFilterChanged"
                ></b-form-input>
                <b-input-group-append v-if="quickFilterText">
                  <b-button variant="outline-secondary" class="border-secondary py-0" @click="quickFilterText = ''; onQuickFilterChanged()">
                    <i class="fa fa-times small"></i>
                  </b-button>
                </b-input-group-append>
              </b-input-group>

              <b-button variant="outline-secondary" size="sm" @click="refreshWatchlist" :disabled="loading">
                <i class="fa fa-refresh" :class="{'fa-spin': loading}"></i>
              </b-button>
            </div>
          </b-card-header>
          
          <ag-grid-vue class="ag-theme-balham-dark" style="width: 100%; height: 60vh"
                       :gridOptions='gridOptions'
                       :getContextMenuItems="getContextMenuItems"
                       :isExternalFilterPresent="isExternalFilterPresent"
                       :doesExternalFilterPass="doesExternalFilterPass"
                       @selection-changed="onSelectionChanged"
                       @cell-value-changed="onCellValueChanged"
                       :rowData="watchlistData"></ag-grid-vue>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>

import {mapGetters} from 'vuex'
import {DefaultGridOptions} from "@/default_table_settings";
import {AgGridVue} from 'ag-grid-vue'
import { applyGridPersistence } from "@/grid_persistence"
import moment from "moment";

export default {
  name: 'watchlist',
  components: {
    'ag-grid-vue': AgGridVue
  },
  computed: {
    ...mapGetters({
      watchlist: 'Watchlist'
    }),
    watchlistData() {
      if (this.watchlist && this.watchlist.watchlist) {
        return this.watchlist.watchlist;
      }
      return [];
    }
  },
  data() {
    return {
      userr: '',
      note: '',
      priority: false,
      loading: false,
      selectedRows: [],
      quickFilterText: '',
      filterType: 'Show All',
      showOnlyPriority: false,
      gridOptions: null,
      reason: null,
      serverOptions: [
        { text: 'All', value: 'Show All' },
        { text: 'Gold', value: 'Gold' },
        { text: 'Silver', value: 'Silver' },
        { text: 'Online', value: 'Any' }
      ],
      options: [
        {text: 'Select Reason', value: null},
        'Bot', 'Macro', 'Speedhack', 'RMT', 'Bug abuse', 'Scamming', 'Other'
      ]
    }
  },
  methods: {
    onQuickFilterChanged() {
      this.gridOptions.api.setQuickFilter(this.quickFilterText);
    },
    onSelectionChanged() {
      this.selectedRows = this.gridOptions.api.getSelectedRows();
    },
    refreshWatchlist() {
      this.loading = true;
      this.emit('getWatchlist', {});
      setTimeout(() => { this.loading = false; }, 800);
    },
    togglePriorityFilter() {
      this.showOnlyPriority = !this.showOnlyPriority;
      this.activeExternalFilter();
    },
    doesExternalFilterPass(node) {
      if(node.data) {
        // Priority Filter
        if (this.showOnlyPriority && !node.data.priority) {
          return false;
        }

        // Server/Online Filter
        if(this.filterType === 'Show All')
          return true;
        
        if(this.filterType === 'Gold') {
          return node.data.servers && node.data.servers.gold
        }
        else if(this.filterType === 'Silver') {
          return node.data.servers && node.data.servers.silver
        }
        else if(this.filterType === 'Any') {
          return node.data.servers && (node.data.servers.silver || node.data.servers.gold)
        }
      }
      return true;
    },
    isExternalFilterPresent() {
      return this.filterType !== 'Show All' || this.showOnlyPriority;
    },
    activeExternalFilter() {
      this.gridOptions.api.onFilterChanged();
    },
    AddWatchlist() {
      if (!this.userr || !this.reason) return;
      const usernames = this.userr.trim().split('\n').map(u => u.trim()).filter(u => u.length > 0);
      if (usernames.length === 0) return;

      this.emit('addWatchlist', {
        'username': usernames,
        'reason': this.reason,
        'note': this.note,
        'priority': this.priority
      });

      this.showToast('success', 'Watchlist updated', `Processing ${usernames.length} users...`);
      this.userr = '';
      this.note = '';
      this.reason = null;
      this.priority = false;
    },
    deleteSelected() {
      if (this.selectedRows.length === 0) return;

      const names = this.selectedRows.map(r => r.username);
      const msg = names.length === 1
        ? `Remove <b>${names[0]}</b> from the watchlist?`
        : `Remove <b>${names.length}</b> selected users from the watchlist?`;

      this.$bvModal.msgBoxConfirm('', {
        title: 'Confirm Deletion',
        messageHtml: msg, 
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'YES, Remove',
        cancelTitle: 'Cancel',
        centered: true,
        headerClass: 'p-2 border-secondary bg-dark text-white',
        footerClass: 'p-2 border-secondary bg-dark',
        bodyClass: 'bg-dark text-white py-3 text-center'
      })
      .then(value => {
        if (value) {
          names.forEach(name => {
            this.emit('deleteWatchlist', name);
          });
          this.showToast('info', 'Deleted', `${names.length} users removed.`);
        }
      });
    },
    deleteSingle(name) {
      this.$bvModal.msgBoxConfirm(`Remove ${name} from the watchlist?`, {
        title: 'Confirm Deletion',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'YES',
        cancelTitle: 'NO',
        centered: true,
        headerClass: 'p-2 border-secondary bg-dark text-white',
        footerClass: 'p-2 border-secondary bg-dark',
        bodyClass: 'bg-dark text-white'
      })
      .then(value => {
        if (value) {
          this.emit('deleteWatchlist', name);
          this.showToast('info', 'Deleted', `${name} removed.`);
        }
      });
    },
    getContextMenuItems: function getContextMenuItems(params) {
      let self = this;
      const selectedCount = self.selectedRows.length;

      return [
        'copy',
        'copyWithHeaders',
        'separator',
        {
          name: selectedCount > 1 ? `Delete ${selectedCount} selected` : 'Delete from Watchlist',
          icon: '<i class="fa fa-trash text-danger"/>',
          disabled: !self.hasPermission(this.WatchlistDelete),
          action: function () {
            if (selectedCount > 1) {
              self.deleteSelected();
            } else {
              self.deleteSingle(params.node.data.username);
            }
          }
        },
        'separator',
        'export'
      ];
    },
    createColumnDefs: function () {
      let self = this
      return [
        {
          headerName: '',
          field: 'priority',
          width: 50,
          sortable: true,
          suppressMenu: true,
          cellRenderer: (params) => {
            const icon = params.value ? 'fa-star text-warning' : 'fa-star-o text-muted opacity-50';
            const title = params.value ? 'High Priority (Click to demote)' : 'Normal Priority (Click to promote)';
            
            const wrapper = document.createElement('div');
            wrapper.style.display = 'flex';
            wrapper.style.alignItems = 'center';
            wrapper.style.justifyContent = 'center';
            wrapper.style.height = '100%';
            
            const i = document.createElement('i');
            i.className = `fa ${icon} priority-star`;
            i.title = title;
            i.style.cursor = 'pointer';
            i.style.fontSize = '1.2rem';
            
            i.addEventListener('click', (e) => {
              e.stopPropagation();
              const newPriority = !params.data.priority;
              
              // Optimistic UI Update: change icon immediately
              i.className = `fa ${newPriority ? 'fa-star text-warning' : 'fa-star-o text-muted opacity-50'} priority-star`;
              params.node.setDataValue('priority', newPriority);

              self.emit('addWatchlist', {
                'username': [params.data.username],
                'note': params.data.note,
                'reason': params.data.reason,
                'priority': newPriority
              });
              self.showToast('success', 'Priority Changed', `${params.data.username} is now ${newPriority ? 'High Priority' : 'Normal Priority'}.`);
            });
            
            wrapper.appendChild(i);
            return wrapper;
          }
        },
        {
          headerName: 'Username',
          field: 'username',
          filter: 'agTextColumnFilter',
          checkboxSelection: true,
          headerCheckboxSelection: true,
          width: 200,
          cellRenderer: (params) => {
            if (!params.value) return '';
            const route = "/user/" + params.value + "/profile";
            const link = document.createElement("a");
            link.href = self.$router.resolve(route).href;
            link.innerText = params.value;
            link.className = "font-weight-bold text-primary";
            link.addEventListener("click", e => {
              e.preventDefault();
              self.$router.push(route);
            });
            return link;
          }
        },
        {
          headerName: 'Staff',
          field: 'staff',
          filter: 'agTextColumnFilter',
          width: 120
        },
        {
          headerName: 'Reason',
          field: 'reason',
          filter: 'agTextColumnFilter',
          width: 140
        },
        {
          headerName: 'Note',
          field: 'note',
          editable: true,
          filter: 'agTextColumnFilter',
          flex: 1,
          cellClass: 'editable-cell',
          tooltipValueGetter: () => 'Double-click to edit note'
        },
        {
          headerName: 'Added',
          field: 'date',
          sort: 'desc',
          width: 160,
          cellRenderer: params => self.formatDateDisplay(params.value),
        },
        {
          headerName: 'Status',
          field: 'status',
          width: 110,
          sortable: false,
          cellRenderer: (params) => {
            if (!params.data.servers) return '<span class="text-muted small">Offline</span>';
            const online = Object.entries(params.data.servers)
              .filter(([key, value]) => value)
              .map(([key]) => {
                const variant = key.toLowerCase();
                return `<span class="badge badge-${variant} text-uppercase px-2">${key[0]}</span>`;
              })
              .join(' ');
            return online || '<span class="text-muted small">Offline</span>';
          },
          valueGetter: (val) => {
            if (!val.data.servers) return '';
            return Object.entries(val.data.servers).filter(([k,v]) => v).map(([k]) => k).join(',');
          }
        }
      ]
    },
    onCellValueChanged(params) {
      if (params.oldValue !== params.newValue) {
        this.emit('addWatchlist', {
          'username': [params.data.username],
          'note': params.newValue,
          'reason': params.data.reason,
          'priority': params.data.priority
        });
        this.showToast('success', 'Updated', `Note for ${params.data.username} saved.`);
      }
    },
    showToast(type, title, message) {
      if (this.$notifications) {
        this.$notifications[type]({title, message});
      }
    }
  },
  beforeMount() {
    this.gridOptions = Object.assign({}, DefaultGridOptions, {
      rowSelection: 'multiple',
      suppressRowClickSelection: true,
      getRowId: (params) => params.data.username,
      columnDefs: this.createColumnDefs(),
      onGridReady: (params) => {
      },
      rowHeight: 40
    })
    applyGridPersistence(this.gridOptions, 'watchlist-grid')
  },
  mounted() {
    this.emit('getWatchlist', {})
  }
}
</script>

<style scoped>
.grid-search-group {
  width: 200px;
}
.priority-star {
  transition: transform 0.1s ease-in-out;
}
.priority-star:hover {
  transform: scale(1.3);
  color: #ffc107 !important;
}
.editable-cell {
  background-color: rgba(255, 255, 255, 0.02) !important;
  cursor: pointer;
  border-left: 2px solid transparent;
  transition: all 0.2s;
}
.editable-cell:hover {
  background-color: rgba(255, 255, 255, 0.05) !important;
  border-left: 2px solid #20a8d8;
}
.ag-theme-balham-dark .ag-row-selected {
  background-color: rgba(0, 123, 255, 0.1) !important;
}
.border-dashed {
  border-style: dashed !important;
}
.dark-radio-group .btn-outline-secondary {
  border-color: #4f5d73;
  color: #a4b7c1;
}
.dark-radio-group .btn-outline-secondary.active {
  background-color: #20a8d8 !important;
  border-color: #20a8d8 !important;
}
.fade-enter-active, .fade-leave-active {
  transition: opacity .3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>